import type { ProcessSection, ProcessStatus, ProcessWorkflow } from "../../../api";
import type { StageDraft } from "../types";
import { isConditional } from "../conditionText";
import { phasesOf, type Phase } from "../lifecycle";

/**
 * Where everything sits on the canvas.
 *
 * **No node position is ever stored.** Every coordinate here is computed from
 * stage order and conditions, so the canvas is a pure view of the model and
 * cannot drift out of step with what the engine will do. Storing coordinates
 * would add a second source of truth and a migration every time the shape of a
 * process changes -- and it would let a diagram *look* like a process the
 * engine would never walk, which is the one thing a process designer must
 * never do.
 *
 * That is also why dragging a node reorders the process rather than moving it:
 * the drop position is read as an index and then thrown away.
 */

/** Wide enough that real section and sign-off names stop truncating.
 *  A step card is fixed-width and grows *taller* with its contents, so width is
 *  pure gain here: it costs no vertical room, and the canvas had horizontal
 *  space going spare on any ordinary monitor. */
export const NODE_WIDTH = 380;
/** Height of a stage card with no contents. */
export const NODE_BASE_HEIGHT = 68;
/** Each section / sign-off chip listed inside the card. */
export const CHIP_HEIGHT = 30;
/** The "applies when" strip along the bottom of a conditional card. */
export const CONDITION_STRIP = 26;
/** Vertical clearance between two cards — the insert affordance lives here. */
export const GAP = 56;
/** Where the spine sits. Room to the left for bypass routing. */
export const SPINE_X = 260;
/** How far a conditional step is indented, to leave its bypass a channel. */
export const BRANCH_INDENT = 44;
/** Width of one bypass channel to the left of the spine. */
export const BYPASS_GUTTER = 34;
/** The start/end pills are narrower than a stage card. */
export const PILL_WIDTH = 180;
export const PILL_HEIGHT = 40;

export type LaidOutStage = {
  stage: StageDraft;
  index: number;
  x: number;
  y: number;
  height: number;
  conditional: boolean;
  /** Which bypass channel this step's skip-edge uses; 0 is nearest the spine. */
  channel: number;
};

export type Lane = Phase & {
  y: number;
  height: number;
};

export type Layout = {
  stages: LaidOutStage[];
  lanes: Lane[];
  /** Total drawing height, so the lane backdrop can be sized. */
  height: number;
  startY: number;
  endY: number;
  /** Drawing width, at least the content and at most the visible pane. */
  width: number;
};

/**
 * How tall a stage card is.
 *
 * Derived rather than fixed because the card lists what is inside it: a step
 * holding four sections has to be four chips tall, and a fixed height would
 * either clip it or leave every simple step mostly empty.
 */
export const heightOf = (stage: StageDraft): number => {
  const chips =
    stage.kind === "DataEntry" ? stage.form_ids.length : stage.workflow_ids.length;
  // An empty step still shows its "nothing here yet" row, so the minimum is one.
  const rows = Math.max(chips, 1);
  return (
    NODE_BASE_HEIGHT +
    rows * CHIP_HEIGHT +
    (isConditional(stage.condition) ? CONDITION_STRIP : 0)
  );
};

/**
 * Assign each conditional step a bypass channel.
 *
 * One optional step is easy: arc from the step before it to the step after it.
 * Two *adjacent* optional steps is where that falls apart -- both arcs would
 * start and end in nearly the same place, overlap, and between them be unable
 * to express "both were skipped". Real processes do this constantly (an HSE
 * assessment and an environmental assessment, each triggered by its own
 * answer), so it is not an edge case to defer.
 *
 * The fix is to give overlapping bypasses their own vertical channels to the
 * left of the spine, the way a subway map separates lines that run together.
 * A channel is free again once its arc has ended, so a process with optional
 * steps spread out uses one channel for all of them rather than marching
 * steadily leftwards.
 */
export const assignChannels = (stages: StageDraft[]): number[] => {
  const channels: number[] = new Array(stages.length).fill(-1);
  /** For each channel, the stage index its current arc ends at. */
  const busyUntil: number[] = [];

  stages.forEach((stage, index) => {
    if (!isConditional(stage.condition)) return;
    // The arc spans from the gap above this step to the gap below it.
    const start = index - 1;
    const end = index + 1;
    let channel = busyUntil.findIndex((until) => until <= start);
    if (channel === -1) {
      channel = busyUntil.length;
      busyUntil.push(end);
    } else {
      busyUntil[channel] = end;
    }
    channels[index] = channel;
  });

  return channels;
};

export const computeLayout = (
  stages: StageDraft[],
  statuses: ProcessStatus[],
  /** Visible canvas width in flow units, so the lane bands can fill it. */
  paneWidth = 0
): Layout => {
  const channels = assignChannels(stages);
  const laid: LaidOutStage[] = [];

  let y = PILL_HEIGHT + GAP;
  const startY = 0;

  stages.forEach((stage, index) => {
    const conditional = isConditional(stage.condition);
    const height = heightOf(stage);
    laid.push({
      stage,
      index,
      // A conditional step is indented so the eye can see, without reading a
      // word, that the spine passes it by.
      x: SPINE_X + (conditional ? BRANCH_INDENT : 0),
      y,
      height,
      conditional,
      channel: channels[index],
    });
    y += height + GAP;
  });

  const endY = y;

  // Lanes span the steps that run under one lifecycle state, from the top of
  // the first card's gap to the bottom of the last one's.
  const lanes: Lane[] = phasesOf(stages, statuses).map((phase) => {
    const first = laid[phase.from];
    const last = laid[phase.to];
    const top = first.y - GAP / 2;
    const bottom = last.y + last.height + GAP / 2;
    return { ...phase, y: top, height: bottom - top };
  });

  return {
    stages: laid,
    lanes,
    height: endY + PILL_HEIGHT,
    startY,
    endY,
    width: Math.max(contentWidth(), paneWidth),
  };
};

/** Widest point the *content* reaches. */
const contentWidth = (): number =>
  SPINE_X + BRANCH_INDENT + NODE_WIDTH + 80;

/**
 * How wide to draw the lane bands.
 *
 * A band narrower than the canvas reads as a box sitting on the page rather
 * than a phase the whole process passes through, which is the opposite of what
 * it is for -- and it was the visible symptom of the canvas not using the width
 * it had. So bands stretch to the visible pane when that is wider than the
 * content. Still derived, still stored nowhere.
 */
export const layoutWidth = (layout: Layout): number =>
  Math.max(contentWidth(), layout.width ?? 0);

/** Where a bypass channel's vertical run sits. */
export const channelX = (channel: number): number =>
  SPINE_X - 24 - channel * BYPASS_GUTTER;

/**
 * Which step a node dropped at `y` should become.
 *
 * Reading the drop position as an index is what keeps dragging honest: the
 * pixel position is never stored, it only answers "which slot in the order did
 * they mean?". Uses the midpoint of each card so a node dropped anywhere over
 * a neighbour takes its place.
 */
export const indexAtY = (layout: Layout, y: number, ignore: number): number => {
  const centres = layout.stages
    .filter((s) => s.index !== ignore)
    .map((s) => ({ index: s.index, centre: s.y + s.height / 2 }));

  let target = 0;
  centres.forEach(({ centre }) => {
    if (y > centre) target += 1;
  });
  return Math.max(0, Math.min(layout.stages.length - 1, target));
};

/** Sections and sign-offs that are not in any step yet. */
export const looseItems = (
  stages: StageDraft[],
  sections: Record<number, ProcessSection>,
  workflows: ProcessWorkflow[]
) => {
  const placedForms = new Set(stages.flatMap((s) => s.form_ids));
  const placedWorkflows = new Set(stages.flatMap((s) => s.workflow_ids));
  return {
    sections: Object.values(sections).filter((s) => !placedForms.has(s.id)),
    workflows: workflows.filter(
      (w) => w.id != null && !placedWorkflows.has(w.id)
    ),
  };
};
